import { useCoachQuery } from '@/features/home/queries';
import { summaryResetStore } from '@/features/player-session/components/session-summary/summary-card-cache-state';
import { useListPlayersSessionQuery } from '@/features/player-session/queries';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { useOverviewSessionUiStore } from '@/features/player-session/stores/overview-session-ui-store';
import { usePlayerFilters } from '@/features/player/context/player-filters-context';
import { usePlayerSort } from '@/features/player/context/player-sort-context';
import { lastActiveSecondsByIndex } from '@/features/player/utils/helpers';
import { useSessionStore } from '@/stores';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';

export const useOverviewSessions = (options?: {
  limit?: number;
  pagination?: boolean;
  featureKey?: string;
}) => {
  const { limit = 50, pagination, featureKey } = options ?? {};
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [forceUpdateNonce, setForceUpdateNonce] = useState(0);

  const user = useSessionStore(useShallow(s => s.user));
  const { key: sort, order, reset: resetSort } = usePlayerSort();
  const { data: coach } = useCoachQuery(user?.uid);
  const sessionId = coach?.active_session_id;
  const qc = useQueryClient();
  const { level, lastActive, reset: resetFilters } = usePlayerFilters();
  const resetNonce = useOverviewSessionUiStore(s => s.resetNonce);
  const refreshNonce = useOverviewSessionUiStore(s => s.refreshNonce);
  const handledRefreshNonceRef = useRef<number>(0);

  const lastActiveSeconds = lastActiveSecondsByIndex(lastActive);

  const resetAll = useCallback(() => {
    setSearchText('');
    setPage(1);
    resetSort();
    resetFilters();
  }, [resetFilters, resetSort]);

  useEffect(() => {
    return () => {
      resetSort();
    };
  }, [resetSort, user?.uid]);

  useEffect(() => {
    resetAll();
    // Only when session changes (start/continue), we want a clean list state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (resetNonce === 0) return;
    resetAll();
  }, [resetAll, resetNonce]);

  const {
    data: paginatedSessions,
    isFetching,
    isLoading,
    refetch,
    isRefetching,
  } = useListPlayersSessionQuery(
    {
      search: searchText,
      sort: sort
        ? (`${sort}${order.charAt(0).toUpperCase()}${order.slice(1)}` as player.SortBy)
        : undefined,
      pagination: pagination ?? true,
      sessionId,
      filterInactiveFor: lastActiveSeconds,
      levelFilter: level,
      resultsPerPage: limit,
      expectedPage: page,
    },
    featureKey,
  );
  const listPlayer = paginatedSessions?.data ?? [];
  const paginatedMeta = paginatedSessions?.meta;

  useEffect(() => {
    if (!sessionId) return;
    if (refreshNonce === 0) return;
    // Run at most once per nonce (refetch fn identity can change between renders).
    if (handledRefreshNonceRef.current === refreshNonce) return;
    handledRefreshNonceRef.current = refreshNonce;
    qc.invalidateQueries({ queryKey: playerSessionKeys.all });
  }, [qc, refreshNonce, sessionId]);

  const onRefetch = () => {
    refetch();
    summaryResetStore.reset();
    setForceUpdateNonce(prev => prev + 1);
  };

  return {
    listPlayer,
    paginatedMeta,
    isFetching,
    isLoading,
    refetch: onRefetch,
    isRefetching,
    searchText,
    sort,
    order,
    level,
    lastActive,
    page,
    onChangePage: setPage,
    onChangeSearchText: setSearchText,
    forceUpdateNonce,
    resetAll,
  };
};

export type UseOverviewSessionsReturnType = ReturnType<typeof useOverviewSessions>;
